<template><section><!-- ---
title: JS基础系列之从URL输入到渲染
date: 2022-10-29
tags: JS基础系列
set: BaseJS
--- -->
<p>URL从输入到渲染整个过程可以分为<code v-pre>网络通信</code>和<code v-pre>页面渲染</code>两个方面。每个环节都有优化的空间，都可以称为页面性能提升的关键点。</p>
<h3>一. 网络通信</h3>
<p>浏览器将接收到的<code v-pre>URL</code>中抽取出域名字段，就是访问的主机名，例如<code v-pre>http://www.qq.com</code>，会向域名服务器发起请求获取域名对应的<code v-pre>IP</code>地址，就可以向该<code v-pre>IP</code>地址定位的<code v-pre>HTTP</code>服务器发起<code v-pre>TCP</code>连接。</p>
<h4>1. DNS解析</h4>
<div style="display:flex;justify-content:center;"><img src="https://zhangmingemma.github.io/dist/images/2022-10-29/2.png" style="display:inline-block; margin-bottom:16px;"></div>
<p>DNS解析过程涉及很多步骤，我们在之前的文章<a href="https://zhangmingemma.github.io/#/post?file=2022-10-16-DNS" target="__blank">《计算机网络系列之DNS以及网络域名解析》</a>有介绍过DNS的域名服务器和解析的过程：</p>
<ul>
<li>浏览器缓存：当用户通过浏览器访问域名时，浏览器首先会在自己缓存中查找是否有域名对应的<code v-pre>IP地址</code>，如果有缓存则直接得到<code v-pre>IP地址</code>发起请求，否则继续下一步</li>
<li>本地服务器：向本地服务器，即系统<code v-pre>Hosts</code>文件查询缓存</li>
<li>根域名服务器：向根域名服务器<code v-pre>.com</code>查询<code v-pre>IP地址</code></li>
<li>二级域名服务器：向二级域名服务器<code v-pre>.qq.com</code>查询<code v-pre>IP地址</code></li>
<li>三级域名服务器：向三级域名服务器<code v-pre>www.qq.com</code>查询<code v-pre>IP地址</code></li>
</ul>
<h3>二. 页面渲染</h3>
<h4>1. 进程与线程的概念</h4>
<h5>(1) 进程</h5>
<p>CPU是计算机的核心，承担所有的计算任务，<code v-pre>进程</code>是<code v-pre>CPU</code>资源分配的最小单位，是一个可以独立运行且拥有自己的资源空间的任务程序。<code v-pre>CPU</code>给每个<code v-pre>进程</code>分配资源空间，分出去越多，越卡，每个<code v-pre>进程</code>之间是相互独立的，在运行一个<code v-pre>进程</code>时，其他<code v-pre>进程</code>都处于非运行状态，使用<code v-pre>时间片轮转调度算法</code>来实现同时运行多个进程</p>
<h5>(2) 线程</h5>
<p><strong>线程</strong>是<code v-pre>CPU</code>调度的最小单元，是建立在<code v-pre>进程</code>基础上的一次程序运行单位，通俗来讲，就是程序中的一个执行流，一个<code v-pre>进程</code>可以由多个<code v-pre>线程</code>。一个<code v-pre>进程</code>只有一个<code v-pre>线程</code>的时候，被称作<code v-pre>单线程</code>，程序执行时，所走的程序路径按照连续顺序排下来，前面的处理好，后面的才会执行。一个<code v-pre>进程</code>包含多个<code v-pre>线程</code>的时候，被称作<code v-pre>多线程</code>。</p>
<h5>(3) JS为单线程</h5>
<p>JS作为浏览器脚本语言，主要用途是与用户互动，以及操作<code v-pre>DOM</code>，这就决定了它只能是单线程，否则会带来复杂的同步问题。<code v-pre>HTML5</code>提出<code v-pre>web worker</code>标准，允许<code v-pre>JS</code>脚本创建多线程，但子线程完全受主线程控制的，不能操作<code v-pre>DOM</code>，本质上没有改变<code v-pre>JS</code>的单线程的本质</p>
<h5>(4) 浏览器渲染进程</h5>
<p>浏览器渲染进程也就是常说的浏览器内核，主要作用为页面渲染、脚本执行和事件处理等。这个渲染进程是多线程的</p>
<ul>
<li><strong>GUI渲染线程</strong>：负责渲染浏览器页面，解析<code v-pre>HTML</code>、<code v-pre>CSS</code>，构建<code v-pre>DOM树</code>和<code v-pre>RenderObject</code>树，布局和绘制等</li>
<li><strong>JS引擎线程</strong>：就是JS内核，负责处理JS脚本程序，JS是单线程运行的，并且<strong>JS引擎线程</strong>和<strong>GUI渲染线程</strong>是互斥的，JS引擎线程会阻塞GUI渲染线程，比如浏览器渲染的时候遇到<code v-pre>&lt;script&gt;</code>标签，就会停止GUI渲染，然后JS线程开始运行代码，JS引擎线程停止工作，GUI线程才会继续渲染下面的内容。</li>
<li><strong>事件触发线程</strong>：用来控制事件循环，并且管理着一个事件队列，当JS执行碰到事件绑定和一些异步操作，会走事件触发线程将对应的事件添加到对应的线程中，等异步事件有了结果，便把他们的回调操作添加到事件队列，等待JS引擎线程空闲时来处理，</li>
<li><strong>定时触发器线程</strong>：就是<code v-pre>SetTimeout</code>和<code v-pre>SetInterval</code>线程，用来计时，将回调函数加入单独的线程，计时结束等JS引擎线程空闲后执行</li>
<li><strong>异步请求线程</strong>：在<code v-pre>XHR</code>连接后在浏览器新开一个线程请求，检测到<code v-pre>XHR</code>状态变更时，如果设置有回调函数，异步线程就会有状态变更事件，将这个回调再放入事件队列中由JS引擎线程执行。</li>
</ul>
<h4>2. 渲染过程</h4>
<div style="display:flex;justify-content:center;"><img src="https://zhangmingemma.github.io/dist/images/2022-10-29/3.png" style="display:inline-block; margin-bottom:16px;"></div>
<h5>(1) 解析HTML构建DOM树</h5>
<div style="display:flex;justify-content:center;"><img src="https://zhangmingemma.github.io/dist/images/2022-10-29/4.png" style="display:inline-block; margin-bottom:16px;"></div>
<ul>
<li><strong>字节流编码</strong>：浏览器拿到页面<code v-pre>HTML</code>内容，这个内容是<code v-pre>0/1</code>原始的字节流，需要先用文件的指定编码将它们转换为各个字符，也就是一大串字符串。</li>
<li><strong>词法解析</strong>：也就是从字符串解析出编译的最小单元，如标签开始、标签结束、属性、注释、CDATA节点等。把这些解析出来，父子关系构建好，最终就构成了<code v-pre>DOM树</code>。</li>
</ul>
<h5>(2) 解析CSS构建CSSOM树</h5>
<ul>
<li><strong>请求CSS资源</strong>：构建<code v-pre>DOM树</code>，也就是解析HTML的时候，如果遇到了<code v-pre>link</code>，就会立即发起对资源的请求，返回样式内容，也就是字节流</li>
<li><strong>构建CSSOM</strong>：将CSS文件字符串，进行词法解析，生成CSSOM，决定着DOM结构的样式，即使是<code v-pre>DOM</code>构建先完成，也要等<code v-pre>CSSOM</code>构建完成，才能进行下一步</li>
</ul>
<blockquote>
<p>这里涉及到一个针对CSS样式的优化点：减少使用标签定义样式，避免多层嵌套，多使用class和id来提升CSS渲染构建的效率</p>
</blockquote>
<h5>(3) 解析JS脚本</h5>
<p>构建<code v-pre>DOM</code>树的过程中，如果遇到了<code v-pre>script</code>标签的时候，立即会阻塞<code v-pre>DOM</code>树的构建，将控制权交回给JS引擎线程，等到JS引擎线程执行完毕，浏览器才会从中断的地方恢复DOM树的构建。</p>
<blockquote>
<p>这里涉及到一个针对JS脚本解析的优化点：一般都将JS文件放到<code v-pre>HTML</code>底部进行加载，或是对JS文件执行<code v-pre>async</code>和<code v-pre>defer</code>加载
async：异步执行，不保证执行顺序，一定在onload前，但不确定在<code v-pre>DOMContentLoaded</code>事件的前后
defer：延迟执行，在浏览器看起来的效果像是把JS加载放在了<code v-pre>body</code>后面，虽然按照规范是在<code v-pre>DOMContentLoaded</code>之前，但实际上浏览器优化效果不同，可能在之后</p>
</blockquote>
<h5>(4) 构建渲染树</h5>
<p>渲染树是由<code v-pre>DOM树</code>和<code v-pre>CSSOM树</code>合并而成的，并不是等<code v-pre>DOM树</code>和<code v-pre>CSSOM树</code>加载完成后才开始合并构建<code v-pre>渲染树</code>，三者的构建并无先后条件，也并非完全独立，而是会有交叉，并行构建，因此会一边加载，一边解析，一边渲染。渲染树只包含网页中每个可见元素的布局，并输出绘制流程，将像素渲染到屏幕上。</p>
<h5>(5) 重绘</h5>
<p>元素发生的改变只是影响了元素的一些外观，例如背景色、边框颜色、文字颜色等，此时只需要应用新样式绘制这个元素就可以了</p>
<h5>(6) 回流</h5>
<p>也叫<strong>重排</strong>，<strong>回流</strong>一定伴随着<strong>重绘</strong>，成本较高，引起<strong>回流</strong>的条件包括有：</p>
<ul>
<li>页面渲染初始化</li>
<li><code v-pre>DOM</code>结构改变，比如删除某个节点</li>
<li><code v-pre>render</code>树变化，比如减少<code v-pre>padding</code></li>
<li>窗口<code v-pre>resize</code></li>
<li>元素的width/height/fontSize等发生了变化的时候</li>
<li>读取元素的offset、client、getComputeStyle()等</li>
</ul>
<blockquote>
<p>这里就涉及到了新的优化点：</p>
<ul>
<li>减少逐项更改样式，最好一次性修改，或者将样式组合成class并以此更新</li>
<li>避免循环操作DOM，先创建<code v-pre>documentFragment</code>，在它上面应用所有的<code v-pre>DOM</code>操作，最后再把它添加到<code v-pre>window.document</code>。</li>
<li>避免多次读取<code v-pre>offset</code>等属性，无法避免就要缓存到变量</li>
<li>将复杂的元素绝对定位或者固定定位，脱离文档流，否则回流代价会很高</li>
<li>尽量减少改变字体大小、table布局等，减少回流的可能性</li>
<li>动画中使用<code v-pre>transform</code>和<code v-pre>opacity</code>，不会发生重排和重绘</li>
</ul>
</blockquote>
</section></template>

